import React, { SyntheticEvent } from 'react';
import {
  AutocompleteGetTagProps,
  useAutocomplete,
} from '@mui/base/useAutocomplete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import { autocompleteClasses } from '@mui/material/Autocomplete';
import { Pill } from '../design-system';

interface FilterInputProps {
  parent?: string;
  defaultValue?: any[];
  val?: any[];
  label: string;
  optionLabelProperty?: string;
  options: OptionType[];
  onChange: (e: SyntheticEvent<Element, Event>, value: any) => void;
  renderIndicator?: (option: OptionType) => React.ReactNode;
  ref?: React.RefObject<HTMLInputElement>;
  neutral?: boolean;
}

interface OptionType {
  id: string;
}

interface NeutralStyleProps {
  $neutral?: boolean;
}

const Label = styled('label')<NeutralStyleProps>(
  ({ theme, $neutral }) => `
    padding: 0 0 2px;
    line-height: 1.5;
    font-size: 12px;
    display: block;
    color: ${$neutral ? theme.palette.text.secondary : 'inherit'};
  `
);

const InputWrapper = styled('div')<NeutralStyleProps>(
  ({ theme, $neutral }) => `
  font-size: 14px;
  border: 1px solid ${
    $neutral
      ? theme.palette.softBorder
      : theme.palette.mode === 'dark'
      ? '#434343'
      : '#d9d9d9'
  };
  background-color: ${
    $neutral
      ? theme.palette.sunken
      : theme.palette.mode === 'dark'
      ? '#141414'
      : '#fff'
  };
  border-radius: ${$neutral ? theme.shape.borderRadius * 2 : 4}px;
  box-shadow: ${$neutral ? 'inset 0 1px 2px rgba(0, 0, 0, 0.14)' : 'none'};
  min-height: 40px;
  padding: 3px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;

  &:hover {
    border-color: ${
      $neutral
        ? theme.palette.softBorder
        : theme.palette.mode === 'dark'
        ? '#177ddc'
        : '#40a9ff'
    };
  }

  &.focused {
    border-color: ${
      $neutral
        ? theme.palette.primary.main
        : theme.palette.mode === 'dark'
        ? '#177ddc'
        : '#40a9ff'
    };
    box-shadow: ${
      $neutral
        ? 'inset 0 1px 2px rgba(0, 0, 0, 0.14)'
        : '0 0 0 2px rgba(24, 144, 255, 0.2)'
    };
  }

  & input {
    background-color: ${
      $neutral
        ? 'transparent'
        : theme.palette.mode === 'dark'
        ? '#141414'
        : '#fff'
    };
    color: ${
      $neutral
        ? theme.palette.text.primary
        : theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.65)'
        : 'rgba(0,0,0,.85)'
    };
    height: 30px;
    box-sizing: border-box;
    padding: 4px 6px;
    width: 0;
    min-width: 30px;
    flex-grow: 1;
    border: 0;
    margin: 0;
    outline: 0;
  }
`
);

interface TagProps extends ReturnType<AutocompleteGetTagProps> {
  indicator?: React.ReactNode;
  label: string;
  $neutral?: boolean;
}

interface ListItemProps {
  $isParent: boolean;
  $isParentDefined: boolean;
}

const getEnrichedOptionProps = (
  getProps,
  option,
  options,
  index,
  currentValue,
  parent
) => {
  if (
    !currentValue.length ||
    !parent ||
    !option[parent] ||
    !currentValue.filter((o) => !o[parent]).length ||
    !currentValue.filter((o) => o.id === option[parent]).length
  ) {
    return getProps({ option, index });
  }

  const parentIndex = options.findIndex((o) => o.id === option[parent]);
  const { id, key, onMouseMove, ...parentProps } = getProps({
    option: options[parentIndex],
    index: parentIndex,
  });

  return { ...getProps({ option, index }), ...parentProps };
};

function Tag(props: TagProps) {
  const { indicator, label, onDelete, $neutral, ...other } = props;

  return (
    <Pill {...other} shape="rounded" sx={filterPillStyles}>
      {indicator}
      <span className="FilterInput-tagLabel">{label}</span>
      <CloseIcon onClick={onDelete} />
    </Pill>
  );
}

const filterPillStyles = {
  fontSize: 14,
  fontWeight: 500,
  height: 24,
  minHeight: 24,
  m: '2px',
  py: 0,
  pr: 0.5,
  bgcolor: 'primary.main',
  color: 'primary.contrastText',
  gap: 0.5,
  outline: 0,
  overflow: 'hidden',
  '&:focus': {
    bgcolor: 'primary.dark',
  },
  '& > .FilterInput-tagLabel': {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  '& svg': {
    boxSizing: 'content-box',
    cursor: 'pointer',
    fontSize: 12,
    p: 0.5,
  },
};

const Listbox = styled('ul')<NeutralStyleProps>(
  ({ theme, $neutral }) => `
  width: 100%;
  font-size: 14px;
  margin: 2px 0 0;
  padding: 0;
  position: absolute;
  list-style: none;
  background-color: ${
    $neutral
      ? theme.palette.background.paper
      : theme.palette.mode === 'dark'
      ? '#141414'
      : '#fff'
  };
  overflow: auto;
  max-height: 250px;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1;

  & li {
    display: flex;
    align-items: center;
    gap: 8px;

    & .FilterInput-optionLabel {
      flex-grow: 1;
    }

    & svg {
      color: transparent;
    }
  }

  & li[aria-selected='true'] {
    cursor: pointer;
    background-color: ${
      $neutral
        ? theme.palette.action.selected
        : theme.palette.mode === 'dark'
        ? '#2b2b2b'
        : '#fafafa'
    };
    font-weight: 600;

    & svg {
      color: ${$neutral ? theme.palette.text.primary : '#1890ff'};
    }
  }

  & li.${autocompleteClasses.focused} {
    background-color: ${
      $neutral
        ? theme.palette.action.hover
        : theme.palette.mode === 'dark'
        ? '#003b57'
        : '#e6f7ff'
    };
    cursor: pointer;

    & svg {
      color: currentColor;
    }
  }
`
);

const ListItem = styled('li')<ListItemProps>`
  font-weight: ${(props) =>
    props.$isParent && props.$isParentDefined ? 'bold' : 'normal'};
  padding: ${(props) =>
    props.$isParent || !props.$isParentDefined ? '5px 12px' : '5px 18px'};
`;

const FilterInput = ({
  defaultValue,
  val,
  parent,
  label,
  optionLabelProperty = 'id',
  options,
  onChange,
  renderIndicator,
  neutral = true,
}: FilterInputProps) => {
  const {
    getRootProps,
    getInputLabelProps,
    getInputProps,
    getTagProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
    value,
    focused,
    setAnchorEl,
  } = useAutocomplete({
    defaultValue,
    value: val,
    id: 'filter-builder-input',
    multiple: true,
    onChange,
    options,
    getOptionLabel: (option: OptionType) => option[optionLabelProperty],
  });

  return (
    <div style={{ position: 'relative' }}>
      <div {...getRootProps()}>
        <Label {...getInputLabelProps()} $neutral={neutral}>
          {label}
        </Label>
        <InputWrapper
          ref={setAnchorEl}
          className={focused ? 'focused' : ''}
          $neutral={neutral}
        >
          {value.map((option: OptionType, index: number) => (
            <Tag
              indicator={renderIndicator?.(option)}
              label={option[optionLabelProperty]}
              $neutral={neutral}
              {...getTagProps({ index })}
            />
          ))}
          <input {...getInputProps()} />
        </InputWrapper>
      </div>
      {groupedOptions.length > 0 ? (
        <Listbox {...getListboxProps()} $neutral={neutral}>
          {(groupedOptions as typeof options).map((option, index) => (
            <ListItem
              $isParentDefined={!!parent}
              $isParent={option[parent] === null}
              {...getEnrichedOptionProps(
                getOptionProps,
                option,
                options,
                index,
                value,
                parent
              )}
            >
              {renderIndicator?.(option)}
              <span className="FilterInput-optionLabel">
                {option[optionLabelProperty]}
              </span>
              <CheckIcon fontSize="small" />
            </ListItem>
          ))}
        </Listbox>
      ) : null}
    </div>
  );
};

export { FilterInput };
